import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  darken,
  IconButton,
  makeStyles,
  Paper,
  Popper,
  Theme,
} from '@material-ui/core';
import { CameraAltOutlined } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import { posSendMessage } from 'utils/hooks/useWrapper';

const TEXT_INPUT_TYPE = [
  'text',
  'number',
  'search',
  'password',
  'email',
  'tel',
  'time',
  'url',
];

const useStyles = makeStyles<Theme>(theme => ({
  paper: {
    backgroundColor: darken(theme.palette.background.paper, 0.05),
  },
  arrow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    fontSize: 7,
    width: '3em',
    height: '1em',
    marginBottom: '-1em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '1em 1em 0 1em',
      borderColor: `${darken(
        theme.palette.background.paper,
        0.05,
      )} transparent transparent transparent`,
    },
  },
}));

export function CameraScanTooltip() {
  const { t } = useTranslation('gridButtons');
  const styles = useStyles();

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const lastAnchor = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );
  if (anchor) {
    lastAnchor.current = anchor;
  }

  useEffect(() => {
    function attachAnchor(event: FocusEvent) {
      const focusedElement = event.target as HTMLElement | null;
      if (!focusedElement) {
        return;
      }
      const isTextArea = focusedElement.tagName === 'TEXTAREA';
      const isTextInput =
        focusedElement.tagName === 'INPUT' &&
        TEXT_INPUT_TYPE.includes(focusedElement.getAttribute('type') as string);

      if (isTextArea || isTextInput) {
        setAnchor(focusedElement as HTMLInputElement | HTMLTextAreaElement);
      }
    }

    function detachAnchor(event: FocusEvent) {
      if (event.relatedTarget !== buttonRef.current) {
        setAnchor(null);
        setOpen(false);
      }
    }

    function toggleTooltip() {
      setOpen(prev => !prev);
    }

    document.addEventListener('dblclick', toggleTooltip, true);
    document.addEventListener('focus', attachAnchor, true);
    document.addEventListener('blur', detachAnchor, true);
    return () => {
      document.removeEventListener('dblclick', toggleTooltip);
      document.removeEventListener('focus', attachAnchor);
      document.removeEventListener('blur', detachAnchor);
    };
  }, []);

  return (
    <Popper
      open={!!anchor && open}
      anchorEl={anchor}
      style={{ zIndex: 10000 }}
      placement="top"
      modifiers={{
        flip: {
          enabled: true,
        },
        arrow: {
          enabled: true,
          element: arrowRef,
        },
        offset: {
          offset: '0, 8px',
        },
      }}
    >
      <span className={styles.arrow} ref={setArrowRef} />
      <Paper className={styles.paper}>
        <Button
          ref={buttonRef}
          onClick={() => {
            lastAnchor.current?.focus();
            posSendMessage('scanning:cameraScan')();
          }}
          startIcon={<CameraAltOutlined />}
        >
          {t('functions.cameraScan')}
        </Button>
      </Paper>
    </Popper>
  );
}
